/* eslint-disable max-lines-per-function */
import isNil from 'lodash/isNil.js';
import {
    downloadSolutionsStudentReports,
    downloadSolutionsSummaryReports,
    downloadSolutionsStudentsProgressReports,
    downloadSolutionsStudentsScoreReport,
    getSolutionsReports,
} from '@/api/solutionsReportsApi.js';
// eslint-disable-next-line import/no-cycle
import router from '@/router/index.js';
import { downloadUrl } from '@/api/attachmentsApi.js';
import { getStudentsArchive, getStudentsReport, importFileHomeworkSolutions } from '@/api/homeworkApi.js';
import { getParticipantsReport, importFileParticipants } from '@/api/eventsApi.js';
import store from '@/store/index.js';
import useSnackbar from '@/hooks/snackbars.js';
import { getEchoInstance } from '@/services/echo.js';
import httpStatusCodes from '@/utils/httpStatusCodes.js';
import { ASSIGNMENT_TYPE } from '@/constants/assignments.js';
import downloadIcon from '@/assets/svg/ic_download.svg';
import { getImportErrors } from '@/api/importApi.js';
import { ReportCategories } from '@/constants/reports.js';
import { DEFAULT_HOMEWORK_FILTER_VALUE } from '@/admin/components/Homework/constants.js';
import { homeworkActionsTypes } from './tasks/actions.js';
import { homeworkGettersTypes } from './tasks/getters.js';

const { createSnackbar } = useSnackbar();

/**
 * @param {AxiosResponse} response
 * @param {string} [errorMsg]
 */
const throwIfNotSuccess = (response, errorMsg = 'Ошибка') => {
    if (response?.data?.success === false) {
        throw new Error(errorMsg);
    }
};

export const startSolutionsSummaryReport = async versionId => {
    try {
        const resp = await downloadSolutionsSummaryReports(versionId);
        const { developmentCenterId } = router.currentRoute.value.params;

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Формирование отчета',
            message: 'Сводный отчет поставлен в очередь на создание. Сформированный отчет можно будет получить на странице отчетов.',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });
    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка формирование отчета',
        });
    }
};


export const startSolutionsStudentReport = async versionId => {
    try {
        const resp = await downloadSolutionsStudentReports(versionId);
        const { developmentCenterId } = router.currentRoute.value.params;

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Формирование отчета',
            message: 'Отчет по ученикам поставлен в очередь на создание. Сформированный отчет можно будет получить на странице отчетов.',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });
    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка формирование отчета',
        });
    }
};

export const startSolutionsStudentsProgressReport = async versionId => {
    try {
        const resp = await downloadSolutionsStudentsProgressReports(versionId);
        const { developmentCenterId } = router.currentRoute.value.params;

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Формирование отчета',
            message: 'Отчет об успеваемости поставлен в очередь на создание. Сформированный отчет можно будет получить на странице отчетов.',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });
    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка формирование отчета',
        });
    }
};

export const startSolutionsStudentsScoreReport = async versionId => {
    try {
        const resp = await downloadSolutionsStudentsScoreReport(versionId);
        const { developmentCenterId } = router.currentRoute.value.params;

        throwIfNotSuccess(resp);
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Экспорт добавлен в очередь на обработку',
            message: 'По завершению обработки, экспорт курса в PDF можно скачать на странице отчетов',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });
    } catch (e) {
        console.error(e);

        const { developmentCenterId } = router.currentRoute.value.params;

        createSnackbar({
            type: 'error',
            size: 'large',
            message: 'Ошибка генерации отчета. Попробуйте еще раз',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });
    }
};

/**
 * @param {*} activityId
 * @param {FormData} fileData
 * @return {Promise<void>}
 */
export const startImportingAllSolutions = async (activityId, fileData) => {
    const { developmentCenterId, solutionId, versionId, homeworkId } = router.currentRoute.value.params;

    try {
        store.dispatch('globals/setTemporaryRoute', {
            name: 'TaskMainView',
            params: {
                developmentCenterId,
                solutionId,
                versionId,
                homeworkId,
            },
        });

        const resp = await importFileHomeworkSolutions(activityId, fileData);

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'success',
            size: 'medium',
            message: 'Импорт добавлен в очередь',
        });

    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка импорта. Попробуйте еще раз',
            size: 'large',
            link: {
                text: 'Перейти в модуль проверки',
                route: {
                    name: 'TaskMainView',
                    params: {
                        developmentCenterId,
                        solutionId,
                        versionId,
                        homeworkId,
                    },
                },
            },
        });
    }
};

export const startExportingReports = async activityId => {
    const { developmentCenterId, solutionId, versionId, homeworkId } = router.currentRoute.value.params;

    try {
        const resp = await getStudentsReport(activityId);

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Отчет добавлен в очередь на обработку',
            message: 'По завершению обработки, результаты по практике можно скачать на странице отчетов.',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });

    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка генерации отчета. Попробуйте еще раз',
            size: 'large',
            link: {
                text: 'Перейти в модуль проверки',
                route: {
                    name: 'TaskMainView',
                    params: {
                        developmentCenterId,
                        solutionId,
                        versionId,
                        homeworkId,
                    },
                },
            },
        });
    }
};

export const startExportingArchive = async ({ activityId, developmentCenterId }) => {
    try {
        const resp = await getStudentsArchive(activityId);

        throwIfNotSuccess(resp, 'Ошибка старта формирования архива');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Все решения добавлены в очередь на формирование архива',
            message: 'По завершению формированию архива, результат можно скачать на странице отчетов и архивов',
            link: {
                text: 'Перейти к отчетам и архивам',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });

    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка генерации архива. Попробуйте еще раз',
            size: 'large',
        });
    }
};

export const startImportingAllParticipants = async fileData => {
    const { developmentCenterId, solutionId, versionId, eventId } = router.currentRoute.value.params;

    try {
        store.dispatch('globals/setTemporaryRoute', {
            name: 'EventAttendanceView',
            params: {
                developmentCenterId,
                solutionId,
                versionId,
                eventId,
            },
        });

        const resp = await importFileParticipants({
            versionId,
            file: fileData,
        });

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'success',
            size: 'medium',
            message: 'Импорт добавлен в очередь',
        });

    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка импорта. Попробуйте еще раз',
            size: 'large',
            link: {
                text: 'Перейти в модуль проставления явки',
                route: {
                    name: 'EventAttendanceView',
                    params: {
                        developmentCenterId,
                        solutionId,
                        versionId,
                        eventId,
                    },
                },
            },
        });
    }
};

export const startExportingParticipantsReports = async () => {
    const { developmentCenterId, solutionId, versionId, eventId } = router.currentRoute.value.params;

    try {
        const resp = await getParticipantsReport({ versionId });

        throwIfNotSuccess(resp, 'Ошибка старта формирования отчета');
        createSnackbar({
            type: 'info',
            size: 'large',
            title: 'Отчет добавлен в очередь на обработку',
            message: 'По завершению обработки, результаты по практике можно скачать на странице отчетов.',
            link: {
                text: 'Перейти в раздел',
                route: {
                    name: 'AdminReports',
                    params: {
                        developmentCenterId,
                    },
                },
            },
        });

    } catch (e) {
        console.error(e);
        createSnackbar({
            type: 'error',
            message: 'Ошибка генерации отчета. Попробуйте еще раз',
            size: 'large',
            link: {
                text: 'Перейти к отчетам',
                route: {
                    name: 'EventAttendanceView',
                    params: {
                        developmentCenterId,
                        solutionId,
                        versionId,
                        eventId,
                    },
                },
            },
        });
    }
};

/** @type {import('vuex').Module<any, any>} */
const module = {
    namespaced: true,
    state: {
        fetched: false,
        loading: false,
        reports: [],
        meta: {},
        accessDenied: false,
        listeners: [],
    },
    mutations: {
        startLoading(state) {
            state.loading = true;
        },
        stopLoading(state) {
            state.loading = false;
            state.fetched = true;
        },
        saveReports(state, { reports, meta = {} }) {
            state.reports = reports;
            state.meta = meta;
        },
        setAccessDenied(state, payload) {
            state.accessDenied = payload;
        },
        addListenReportReady(state, callback) {
            state.listeners = [
                ...state.listeners,
                callback,
            ];
        },
        removeListenReportReady(state, callback) {
            state.listeners = state.listeners.filter(cb => cb !== callback);
        },
    },
    actions: {
        callListenersReportReady: ({ state }, payload) => {
            state.listeners.forEach(callback => {
                callback?.(payload);
            });
        },
        initReportChanel({ dispatch }, { userId }) {
            getEchoInstance()
                .private(`users.${userId}.report`)
                .stopListening('.report.ready')
                .listen('.report.ready', reportData => {
                    const routeDevCenterId = router.currentRoute.value.params?.developmentCenterId;
                    const reportDevelopmentCenterId = reportData?.location?.development_center_id;
                    const isReportRoutes = router.currentRoute.value.name === 'AdminReports' &&
                        Number(reportDevelopmentCenterId) === Number(routeDevCenterId);
                    const shouldGoToReports = !isNil(reportDevelopmentCenterId) && !isReportRoutes;
                    const isArchiveReportCategory = reportData.category === ReportCategories.ARCHIVE;

                    const title = isArchiveReportCategory
                        ? 'Все решения сформированы в архив'
                        : 'Отчет сформирован';

                    const message = isArchiveReportCategory
                        ? 'Чтобы скачать архив, перейдите на страницу списка отчётов и архивов. Архив будет доступен в течение 7 дней'
                        : 'Чтобы скачать отчет, перейдите на страницу списка отчётов и архивов.';

                    const downloadText = isArchiveReportCategory ? 'Скачать архив' : 'Скачать отчет';

                    createSnackbar({
                        type: 'success',
                        size: 'large',
                        title,
                        message,
                        links: [
                            ...(shouldGoToReports
                                ? [
                                    {
                                        text: 'Перейти к отчетам и архивам',
                                        route: {
                                            name: 'AdminReports',
                                            params: {
                                                developmentCenterId: reportDevelopmentCenterId,
                                            },
                                        },
                                    },
                                ]
                                : []),
                            {
                                text: downloadText,
                                icon: downloadIcon,
                                on: {
                                    click: () => {
                                        if (reportData.url) {
                                            downloadUrl(reportData.url);
                                        }
                                    },
                                },
                            },
                        ],

                        // TODO
                        // snackbarOptions: {
                        //     id: `report_user_answers_${blockId}`,
                        // },
                    });

                    dispatch('callListenersReportReady', reportData);
                })
                .stopListening('.report.failed')
                .listen('.report.failed', reportData => {
                    createSnackbar({
                        type: 'error',
                        size: 'large',
                        title: 'Ошибка формирования отчета',
                    });

                    dispatch('callListenersReportReady', reportData);
                });
        },
        initSolutionsImportChanel(_, { userId }) {
            getEchoInstance()
                .private(`users.${userId}.homework`)
                .stopListening('.homework.imported')
                .listen('.homework.imported', reportData => {
                    const totalRowsCount = reportData.total_rows_count;
                    const importedRowsCount = reportData.imported_rows_count;
                    const notImported = totalRowsCount - importedRowsCount;
                    const message = notImported > 0 ? `По ${notImported} ученикам не удалось получить данные или практика завершена. Проверьте правильность заполнения или дождитесь пока система получит данные об учениках после их перехода в новый интерфейс курсов` : '';
                    const snackbarTitle = importedRowsCount
                        ? `Успешно импортировано ${importedRowsCount} из ${totalRowsCount}`
                        : `${importedRowsCount} из ${totalRowsCount} импортировано`;
                    const tempRoute = store.getters['globals/getTemporaryRoute'];

                    const isShowDownloadErrorBtn = notImported > 0 && reportData.import_id;

                    if (totalRowsCount && tempRoute?.name === 'TaskMainView') {
                        const homeworkData = store.getters[`tasks/${homeworkGettersTypes.GET_HOMEWORK_BLOCK}`];
                        const filterParams = {
                            ...DEFAULT_HOMEWORK_FILTER_VALUE,
                            homeworkId: reportData.location.activity_id,
                            page: 1,
                            search: '',
                            isReplaceContent: true,
                        };

                        if (homeworkData.assignment_type === ASSIGNMENT_TYPE.GROUP) {
                            store.dispatch(`tasks/${homeworkActionsTypes.FETCH_HOMEWORK_BLOCK_GROUP_DATA}`, filterParams);
                        } else if (homeworkData.assignment_type === ASSIGNMENT_TYPE.INDIVIDUAL) {
                            store.dispatch(`tasks/${homeworkActionsTypes.FETCH_HOMEWORK_BLOCK_DATA}`, filterParams);
                        }
                    }

                    createSnackbar({
                        type: 'info',
                        size: 'large',
                        title: snackbarTitle,
                        message,
                        links: [
                            {
                                text: 'Перейти в модуль проверки',
                                route: {
                                    name: tempRoute?.name,
                                    params: tempRoute?.params,
                                },
                            },
                            ...(isShowDownloadErrorBtn
                                ? [
                                    {
                                        text: 'Скачать ошибки',
                                        icon: downloadIcon,
                                        on: {
                                            click: () => {
                                                const fileUrl = getImportErrors(reportData.import_id);

                                                downloadUrl(fileUrl, '');
                                            },
                                        },
                                    },
                                ]
                                : []),
                        ],
                    });
                })
                .stopListening('.homework.import_failed')
                .listen('.homework.import_failed', () => {
                    const tempRoute = store.getters['globals/getTemporaryRoute'];

                    createSnackbar({
                        type: 'error',
                        size: 'large',
                        title: 'Ошибка импорта. Попробуйте еще раз',
                        link: {
                            text: 'Перейти в модуль проверки',
                            route: {
                                name: tempRoute?.name,
                                params: tempRoute?.params,
                            },
                        },
                    });
                });
        },
        initParticipationReportChannel: (_, { courseId }) => {
            getEchoInstance()
                .private(`event-participation.courses.${courseId}.events-participation.import`)
                .stopListening('.event_participation.imported')
                .listen('.event_participation.imported', importData => {
                    const { imported_rows_count: importedRowsCount, total_rows_count: totalRowsCount, location } = importData;
                    const failedCount = totalRowsCount - importedRowsCount;
                    const snackbarTitle = importedRowsCount
                        ? `Успешно импортировано ${importedRowsCount} из ${totalRowsCount}`
                        : `${importedRowsCount} из ${totalRowsCount} импортировано`;

                    createSnackbar({
                        type: 'info',
                        size: 'large',
                        title: snackbarTitle,
                        message: failedCount
                            ? `По ${failedCount} учащимся не удалось получить данные или практика завершена. Проверьте правильность заполнения или дождитесь пока система получит данные об учениках после их перехода в новый интерфейс курсов`
                            : '',
                        link: {
                            text: 'Перейти в модуль проверки',
                            route: {
                                name: 'EventsMainView',
                                params: {
                                    developmentCenterId: location.development_center_id,
                                    solutionId: location.project_id,
                                    versionId: location.course_id,
                                },
                            },
                        },
                    });
                })
                .stopListening('.event_participation.import_failed')
                .listen('.event_participation.import_failed', importData => {
                    const { location } = importData;

                    createSnackbar({
                        type: 'error',
                        size: 'large',
                        title: 'Ошибка импорта. Попробуйте еще раз',
                        link: {
                            text: 'Перейти в модуль проставления явки',
                            route: {
                                name: 'EventsMainView',
                                params: {
                                    developmentCenterId: location.development_center_id,
                                    solutionId: location.project_id,
                                    versionId: location.course_id,
                                },
                            },
                        },
                    });
                });
        },
        getReports: async ({ commit, state }, payload = {}) => {
            try {
                commit('startLoading');

                const { devCenterId, append = false, ...params } = payload;

                const resp = await getSolutionsReports(devCenterId, params);
                const { data: newReports, meta } = resp.data;
                const reports = append ? [...state.reports, ...newReports] : newReports;

                commit('saveReports', {
                    reports,
                    meta,
                });

                commit('setAccessDenied', false);
            } catch (e) {
                console.error(e);

                if (e.response.status === httpStatusCodes.FORBIDDEN) {
                    commit('setAccessDenied', true);

                    return;
                }

                createSnackbar({
                    type: 'error',
                    message: 'Ошибка получаения списка отчетов',
                });
            } finally {
                commit('stopLoading');
            }
        },
    },
};

export default module;
